// build: arrange — the capstone's centerpiece. config.slots (default 4)
// section slots, each cycling A → B → quiet on tap:
//   A     = the sunlit half (chordLoop + trackMelody, full band)
//   B     = the shadow half (minorLoop + bSection swapped into the mix)
//   quiet = chords only — space is a layer too
// ▶ plays the sections in order via playTrackMix, one cycle each, under the
// saved trackStyle lens (dreamPop when unset). Soft checks run against
// { sections } with the usual "keep it anyway" escape; saving calls
// onKeeper(keeperId, keeperName, { sections: ['A','A','B','A'] }).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { runChecks } from "../../../lib/homeKeyTheory";
import { resumeAudio } from "../../../lib/homeKeyAudio";
import { playTrackMix } from "../../../lib/homeKeyGroove";

const SECTION_CYCLE = ["A", "B", "quiet"];
const SECTION_WORDS = {
  A: "the sunny half",
  B: "the shadow half",
  quiet: "space to breathe",
};

export default function ArrangeBuild({ moment, onReadyChange, onKeeper, keepers }) {
  const config = moment.config || {};
  const slotCount = config.slots || 4;

  const [sections, setSections] = useState(() =>
    Array.from({ length: slotCount }, () => "A")
  );
  const [playingSlot, setPlayingSlot] = useState(-1);
  const [saved, setSaved] = useState(false);

  const playRef = useRef(null);
  const timersRef = useRef([]);
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  const stopPlayback = useCallback(() => {
    if (playRef.current && playRef.current.stop) playRef.current.stop();
    playRef.current = null;
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
    setPlayingSlot(-1);
  }, []);
  useEffect(() => () => stopPlayback(), [stopPlayback]);

  const ready = saved;
  useEffect(() => {
    if (onReadyChange) onReadyChange(ready);
  }, [ready, onReadyChange]);

  const results = useMemo(
    () => runChecks(moment.checks || [], { sections }),
    [moment.checks, sections]
  );
  const allPass = results.every((r) => r.pass);

  const tapSlot = (i) => {
    if (saved) return;
    setSections((prev) => {
      const next = [...prev];
      const at = SECTION_CYCLE.indexOf(next[i]);
      next[i] = SECTION_CYCLE[(at + 1) % SECTION_CYCLE.length];
      return next;
    });
  };

  const all = keepers || {};
  const lensId = (all.trackStyle && all.trackStyle.lensId) || "dreamPop";

  // What plays for one slot: playTrackMix's `section` option knows the
  // material (A = chordLoop + trackMelody, B = minorLoop + bSection, minor
  // mode, drone on E) and skips whatever is missing; the bass sits out of the
  // B section (it was written against the A chords), and `quiet` strips
  // everything back to the chords alone.
  const mixFor = (section) => {
    if (section === "B") {
      return {
        section: "B",
        layers: { bass: false },
      };
    }
    if (section === "quiet") {
      return {
        section: "A",
        layers: { drums: false, bass: false, melody: false, drone: false },
      };
    }
    return { section: "A", layers: {} };
  };

  const playAll = () => {
    if (playRef.current || timersRef.current.length) {
      stopPlayback();
      return;
    }
    resumeAudio();
    const startSlot = (i) => {
      const list = sectionsRef.current;
      if (i >= list.length) {
        stopPlayback();
        return;
      }
      if (playRef.current && playRef.current.stop) playRef.current.stop();
      const mix = mixFor(list[i]);
      const handle = playTrackMix({ keepers: all, lensId, ...mix });
      playRef.current = handle;
      setPlayingSlot(i);
      const cycleMs = (handle && handle.cycleMs) || 4000;
      timersRef.current.push(setTimeout(() => startSlot(i + 1), Math.max(400, cycleMs)));
    };
    startSlot(0);
  };

  const save = () => {
    stopPlayback();
    onKeeper(moment.keeperId, moment.keeperName, { sections: [...sections] });
    setSaved(true);
  };

  const playing = playingSlot >= 0;

  return (
    <div className="hk-moment-body">
      <p>
        {config.prompt ||
          "Lay out your song. Tap a slot to cycle it between the A section, the B section, and a quiet stretch — then play the whole shape in order."}
      </p>

      <div className="hk-slot-row" role="group" aria-label="song sections">
        {sections.map((section, i) => (
          <button
            key={i}
            type="button"
            className={`hk-slot hk-slot--section${
              section === "B" ? " hk-slot--b" : ""
            }${section === "quiet" ? " hk-slot--quiet" : ""}${
              playingSlot === i ? " hk-slot--now" : ""
            }`}
            aria-label={`section ${i + 1}: ${section} — ${SECTION_WORDS[section]}. Tap to change.`}
            onClick={() => tapSlot(i)}
            disabled={saved}
          >
            <span className="hk-slot-degree">
              {section === "quiet" ? "·" : section}
            </span>
            <span className="hk-slot-word">{SECTION_WORDS[section]}</span>
          </button>
        ))}
      </div>

      <div className="hk-choice-row">
        <button
          type="button"
          className={`hk-demo-btn${playing ? " hk-demo-btn--on" : ""}`}
          onClick={playAll}
        >
          {playing ? "■ stop" : "▶ play the sections"}
        </button>
      </div>

      {!saved && results.length > 0 && (
        <div className="hk-checks">
          {results.map((r) => (
            <div
              key={r.id}
              className={`hk-check-note${r.pass ? " hk-check-note--pass" : ""}`}
            >
              {r.pass ? "✓ " : "~ "}
              {r.message}
            </div>
          ))}
        </div>
      )}

      {!saved && (
        <div className="hk-choice-row">
          <button
            type="button"
            className="hk-btn hk-btn--primary"
            disabled={!allPass}
            onClick={save}
          >
            save keeper
          </button>
          {!allPass && (
            <button type="button" className="hk-btn hk-btn--ghost" onClick={save}>
              actually, I like it — keep it anyway
            </button>
          )}
        </div>
      )}

      {saved && (
        <p className="hk-echo-status">
          saved to your track: <strong>{moment.keeperName}</strong>. It's part of
          the music now.
        </p>
      )}
    </div>
  );
}
